import * as _ from "lodash"
import { IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions } from "../PA.Core/PA.Core.IF.BO"
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic"
import { PAdbContext } from "../PA.Core/PAdbContext"
import { IdentityProvider } from "../PA.Core/Basic/Identity"
import { MessageProvider } from "../PA.Core/Basic/Messages"
import { ProgressProvider } from "../PA.Core/Basic/Progress"
import {
    WB_BenchmarkRow,
    WB_CustomerCalendarRow,
    WB_CustomerExternalRelationshipRow,
    WB_CustomerRow, WB_LiftAPIRow,
    WB_SalesOrgRow,
    WB_SourceRow, WB_SourceTypeRow, WB_TargetRow
} from "../PA.Core/PAdbSchemaRows"
import { IwbSalesOrg } from "../PA.Core/PAdbSchemaRowsIF"
import {
    esWB_Benchmark, esWB_Company,
    esWB_Customer,
    esWB_Customer_Calendar,
    esWB_Customer_External_Relationship, esWB_General_Ledger,
    esWB_LiftAPI, esWB_MasterData_Set, esWB_Published_Model,
    esWB_SalesArea,
    esWB_SalesOrg,
    esWB_SalesOrg_Text,
    esWB_Source,
    esWB_SourceType,
    esWB_Target
} from "../PA.Core/PAdbSchema"
import { ESDataStore } from "../PA.Core/lib/ESDataStore"
import { ESRecord } from "../PA.Core/lib/ESRecord"
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver"
import { ASync } from "../PA.Core/Basic/ASync"
import { PAEnvironment } from "../PA.Core/PAEnvironment"
import { SalesOrgBOValidation } from "./SalesOrgBO.Validation"
import { DefaultChanges } from "../PA.Core/BusinessObject/BusinessObjectChanges"
import { CalendarUtils } from "../PA.MD.Services/CalendarUtils"
import { DateUtils } from "../PA.Core/Basic/Dates"


export class SalesOrgBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_SalesOrg: WB_SalesOrgRow

    _FromSide: string
    _sources: WB_SourceRow[]

    constructor(options?: any) {

        this.CreateOptions = options || {}

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({ id: 0, name: "New Business Unit" })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = undefined
        this.LoadProgressProvider = new ProgressProvider("Loading", this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_SalesOrg = null
        this._FromSide = ""
    }

    get WB_SalesOrg(): WB_SalesOrgRow {
        return this.drWB_SalesOrg
    }

    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>
    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start("Creating Business Unit")
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {}
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Business Unit"
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            //Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_SalesOrg)
                .then((newSalesOrgRow: WB_SalesOrgRow) => {
                    newSalesOrgRow.setDefaults()
                    self.drWB_SalesOrg = newSalesOrgRow
                    //add this to all the BOs
                    self.dbContext.add(esWB_SalesOrg, newSalesOrgRow)
                    self.Identity.setAll(self.WB_SalesOrg.cif.WB_SALESORG_ID, self.WB_SalesOrg.cif.SALESORG_ID, self.WB_SalesOrg.cif.SALESORG_NAME)
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    self.Identity.listenToRecordUpdates(self.WB_SalesOrg, "WB_SALESORG_ID", "SALESORG_ID", "SALESORG_NAME")
                    self._bIsLoaded = true
                    return self._AfterBoLoadCompleteInternal()
                })
                .then(() => {
                    self.LoadProgressProvider.finish()

                    self._OnLoadComplete = null
                    resolve(self)
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })
        })
        return this._OnLoadComplete
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this


        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        // if(!this.bIsLoaded)
        //     this.Identity.setAll(anId, anId, "Loading Business Unit (" + anId + ")");

        self.LoadProgressProvider.start("Loading (" + anId + ")")

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {

            if (self.bIsLoaded) {
                const sError = "Sales Org already loaded"
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                reject(sError)
                return
            }

            self.LoadOptions = options || {}
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext()

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Sales Org"
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                reject(sError)
                return
            }
            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = SalesOrgBO.createRecordResolver({ dbContext: self.dbContext })
                recordResolverLoadPromise = SalesOrgBO.setupRecordResolver(recordResolver, [anId])
            }

            if ("From" in self.LoadOptions) {
                // @ts-ignore
                self._FromSide = self.LoadOptions.From
            }

            recordResolverLoadPromise
                .then(function() {
                    const aSalesOrgRow: any = recordResolver.get(esWB_SalesOrg, anId)

                    if (_.isEmpty(aSalesOrgRow)) {
                        self._OnLoadComplete = null
                        self.LoadProgressProvider.exception("Id not found.")
                        resolve(self)
                        return
                    }

                    self.drWB_SalesOrg = aSalesOrgRow
                    aSalesOrgRow.WB_SalesAreaRows.unshift({
                        WB_SALESORG_ID: aSalesOrgRow.cif.WB_SALESORG_ID,
                        WB_SALESAREA_ID: -1,
                        SALESAREA_NAME: "",
                        SALESAREA_ID: "All"

                    })
                    aSalesOrgRow.Selected_SalesArea = -1

                    //source_id
                    _.forEach(self.drWB_SalesOrg.WB_CustomerExternalRelationshipRows, aRow => {
                        if (aRow.isActive)
                            aRow.cif.SOURCE_ID = aRow.WB_CustomerRow_Source.cif.SOURCE_ID || aRow.WB_CustomerRow_Source.WB_SourceRow.cif.SOURCE_ID
                    })


                    self.Identity.setAll(self.WB_SalesOrg.cif.WB_SALESORG_ID, self.WB_SalesOrg.cif.SALESORG_ID, self.WB_SalesOrg.cif.SALESORG_NAME)
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    self.Identity.listenToRecordUpdates(self.WB_SalesOrg, "WB_SALESORG_ID", "SALESORG_ID", "SALESORG_NAME")
                    self._bIsLoaded = true
                    if (self._FromSide == "Calendar")
                        return self._AfterBoLoadCompleteInternal()
                })
                .then(() => {
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.finish()
                    resolve(self)
                })
                .catch(function(err: any) {
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(err)
                    reject(err)
                    return
                })
        })
        return this._OnLoadComplete
    }

    private async _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this

        const arrTypes: WB_SourceTypeRow[] = await self.dbContext.findAll(esWB_SourceType, {
            where: {
                DELETED_IND: { neq: "1" },
                SYSTEM_TYPE: "Consumption"
            }
        })

        const arrSource: WB_SourceRow[] = await self.dbContext.findAll(esWB_Source, {
            where: {
                DELETED_IND: { neq: "1" },
                WB_SOURCETYPE_ID: { inq: _.map(arrTypes, "WB_SOURCETYPE_ID") }
            }
        })


        _.forEach(self.WB_SalesOrg.WB_CustomerCalendarRows, row => {
            let sourceRow = _.first(_.filter(arrSource, aSource => aSource.cif.WB_SOURCE_ID == row.cif.WB_SOURCE_ID))
            let sourceStart = 1
            if (!_.isEmpty(sourceRow) && !_.isEmpty(sourceRow.cif.WEEK_START_DAY_OF_WEEK)) {
                sourceStart = Number(sourceRow.cif.WEEK_START_DAY_OF_WEEK)
            } else {
                if (!_.isEmpty(sourceRow)) {
                    sourceRow.cif.WEEK_START_DAY_OF_WEEK = "1"
                    row.WB_SourceRow = sourceRow
                } else {
                    sourceRow = <WB_SourceRow>self.dbContext.createRecord(esWB_Source)
                    sourceRow.cif.WB_SOURCE_ID = 0
                    sourceRow.cif.WB_MDSET_ID = 0
                    sourceRow.cif.SOURCE_ID = "0"
                    sourceRow.cif.SOURCE_NAME = "None"
                    sourceRow.cif.WEEK_START_DAY_OF_WEEK = "1"
                    row.WB_SourceRow = sourceRow
                }

            }
            const providerStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(sourceRow?.cif?.WEEK_START_DAY_OF_WEEK, 1)
            const retailStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(row.cif.WEEK_START_DAY_OF_WEEK_ACT, 1)

            const offset = DateUtils.DayOfWeek_findNearestOffset(providerStartDOW, retailStartDOW)

            row.cif.CONSUMPTION_DAY_OFFSET = offset

        })

        // @ts-ignore
        arrSource.unshift({ WB_SOURCE_ID: 0, WB_MDSET_ID: 0, SOURCE_ID: "0", SOURCE_NAME: "None" })
        self._sources = arrSource

    }

    get Sources(): WB_SourceRow[] {
        return this._sources
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    get SalesAreaCustomers(): WB_CustomerExternalRelationshipRow[] {
        return _.filter(this.WB_SalesOrg.WB_CustomerExternalRelationshipRows, aRow => aRow.isActive && (aRow.cif.WB_SALESAREA_ID == this.WB_SalesOrg.Selected_SalesArea || this.WB_SalesOrg.Selected_SalesArea == -1))
    }

    get bMapping(): boolean {
        return (this._FromSide == "Mapping")
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this
        self.ValidateMessageProvider.clearMessages()
        self.ValidateProgressProvider.start("Validating " + self.WB_SalesOrg.cif.SALESORG_NAME + "(" + self.WB_SalesOrg.cif.SALESORG_ID + ")")
        return new Promise<IBusinessObject>((resolve, reject) => {
            //Does no validations yet
            self.ValidateOptions = options || {}

            if (self.dbContext == null) {
                const sError = "Unable to determine dbContext for to Validate Business Unit"
                self.ValidateProgressProvider.exception(sError)
                reject(sError)
            }

            SalesOrgBOValidation.Validate(self)
                .then((ignore: any) => {
                    self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0
                    self.ValidateProgressProvider.finish()
                    resolve(self)
                })
                .catch((err: any) => {
                    self._bIsValidated = false
                    self.ValidateProgressProvider.exception(err)
                    resolve(self)
                })
        })
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        const wbSalesOrg = <IwbSalesOrg>self.drWB_SalesOrg
        this.SaveProgressProvider.start("Saving " + wbSalesOrg.SALESORG_NAME + "(" + wbSalesOrg.SALESORG_ID + ")")

        return new Promise<IBusinessObject>((resolve, reject) => {
            //Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false
                const sError = "Unable to determine dbContext to Save SalesOrg."
                self.SaveProgressProvider.exception(sError)
                reject(sError)
                return
            }
            self.SaveOptions = options || {}

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            let bDone = false
            self.Validate({})
                .then((gnore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false
                        bDone = true
                        self.SaveProgressProvider.finish()
                        resolve(self)
                        return
                    }
                    return saveFunction(esWB_SalesOrg, self.drWB_SalesOrg, {})
                        .then((ignore) => {

                            const tasks: Promise<any> [] = []

                            tasks.push(saveFunction(esWB_SalesOrg, self.drWB_SalesOrg, {}))
                            tasks.push(saveFunction(esWB_SalesArea, self.drWB_SalesOrg.WB_SalesAreaRows))
                            tasks.push(saveFunction(esWB_Customer_Calendar, self.drWB_SalesOrg.WB_CustomerCalendarRows))
                            tasks.push(saveFunction(esWB_Customer_External_Relationship, self.drWB_SalesOrg.WB_CustomerExternalRelationshipRows))
                            tasks.push(saveFunction(esWB_LiftAPI, self.drWB_SalesOrg.WB_LiftAPIRows))
                            tasks.push(saveFunction(esWB_Benchmark, self.drWB_SalesOrg.WB_BenchmarkRows))
                            tasks.push(saveFunction(esWB_Target, self.drWB_SalesOrg.WB_TargetRows))
                            return Promise.all(tasks)
                        })
                        .then((ignored) => {
                            if (bDone) return

                            if (bBatch) {
                                self.dbContext.addBatchCompleteListener(() => {
                                    self._bIsSaved = true
                                })
                            } else {
                                self._bIsSaved = true
                            }
                        })
                        .then((ignore) => {
                            self.SaveProgressProvider.finish()
                            resolve(self)
                        })
                        .catch((err) => {
                            self.SaveProgressProvider.exception(err)
                            resolve(self)
                        })
                })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        const wbSalesOrg = <IwbSalesOrg>self.drWB_SalesOrg
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start("Deleting " + wbSalesOrg.SALESORG_NAME + "(" + wbSalesOrg.SALESORG_ID + ")")

        _.forEach(self.WB_SalesOrg.WB_SalesOrgTextRows, aRow => {
            aRow.markDeleted()
        })

        _.forEach(self.WB_SalesOrg.WB_CustomerExternalRelationshipRows, aRow => {
            aRow.markDeleted()
        })

        _.forEach(self.WB_SalesOrg.WB_SalesAreaRows, aRow => {
            aRow.markDeleted()
        })

        _.forEach(self.WB_SalesOrg.WB_CustomerCalendarRows, aRow => {
            aRow.markDeleted()
        })

        return new Promise<IBusinessObject>((resolve, reject) => {
            // 1. Validate if we can delete the SalesOrg

            // 2. If Valid, mark all the records for deletion.
            self.drWB_SalesOrg.markDeleted()

            if (bSaveNow) {
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true
                        self.DeleteProgressProvider.finish()
                        resolve(self)
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err)
                        reject(self)
                    })
            } else {
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
                resolve(self)
            }
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        const opts = options || {}
        opts.dctExcludeFields = opts.dctExcludeFields || {}
        opts.bIgnoreStandardFields = true
        opts.dctIgnoreChanges = opts.dctIgnoreChanges || {}
        opts.bIncludeFieldDetails = true
        opts.dctExcludeHasManyNamedRelations = { [esWB_SalesOrg]: [esWB_SalesArea] }
        opts.dctIncludeBelongsToNamedRelations = {}
        opts.bIncludeFieldDetails = true

        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_SalesOrg, opts)
    }

    addCalender(customerRows: number[]): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {
            const arrCustomers: WB_CustomerRow[] = []

            async function createNewRows() {
                const newRows: WB_CustomerCalendarRow[] = []
                for (let i = 0; i < customerRows.length; i++) {
                    const customerRow = <WB_CustomerRow>await self.dbContext.find(esWB_Customer, customerRows[i])
                    arrCustomers.push(customerRow)
                    const newRow = <WB_CustomerCalendarRow>await self.dbContext.createRecordWithKey(esWB_Customer_Calendar)
                    newRows.push(newRow)
                }
                return newRows
            }

            createNewRows()
                .then((arrNewRow: WB_CustomerCalendarRow[]) => {
                    const arrCustomerRows = _.keyBy(arrCustomers, cust => cust.cif.WB_CUST_ID)
                    for (let i = 0; i < customerRows.length; i++) {
                        const aNewRow = arrNewRow[i]
                        const planningCustomer = arrCustomerRows[customerRows[i]]
                        aNewRow.setDefaults()
                        aNewRow.cif.WB_SALESORG_ID = self.drWB_SalesOrg.cif.WB_SALESORG_ID
                        aNewRow.cif.WB_CUST_ID = planningCustomer.cif.WB_CUST_ID
                        aNewRow.WB_CustomerRow = planningCustomer
                        aNewRow.cif.CONSUMPTION_PERIOD_LEVEL_ID = "Week"
                    }
                    self.dbContext.add(esWB_Customer_Calendar, arrNewRow)
                })
                .then(() => {
                    resolve(self)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    removeCalendar(aWB_CustomerCalendar_ID: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {
            _.forEach(self.drWB_SalesOrg.WB_CustomerCalendarRows, (aRow: WB_CustomerCalendarRow) => {
                if (aRow.isActive && aRow.cif.WB_CUSTCAL_ID == aWB_CustomerCalendar_ID) {
                    aRow.markDeleted()
                }
            })
            resolve(self)
        })
    }

    addExternalCustomer(aWB_PlanningCustomer_Id: number, aWB_ExternalCustomer_Id: number): Promise<IBusinessObject> {
        const self = this
        let arrCust: WB_CustomerRow[] = []
        let sourceRow: WB_SourceRow = undefined

        return new Promise<IBusinessObject>((resolve, reject) => {
            self.dbContext.findUsingCache(esWB_Customer, [aWB_PlanningCustomer_Id, aWB_ExternalCustomer_Id])
                .then((Customers: WB_CustomerRow[]) => {
                    if (_.isEmpty(Customers))
                        return

                    arrCust = Customers
                    return self.dbContext.find(esWB_Source, _.first(_.filter(arrCust, aCust => aCust.cif.WB_CUST_ID == aWB_ExternalCustomer_Id))?.cif?.WB_SOURCE_ID)
                })
                .then((source: WB_SourceRow) => {
                    if (_.isEmpty(arrCust) && _.isEmpty(sourceRow))
                        return
                    sourceRow = source
                    return self.dbContext.createRecordWithKey(esWB_Customer_External_Relationship)
                })
                .then((aNewRow: WB_CustomerExternalRelationshipRow) => {
                    if (_.isEmpty(arrCust) && _.isEmpty(sourceRow))
                        return
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SALESORG_ID = self.drWB_SalesOrg.cif.WB_SALESORG_ID
                    aNewRow.cif.WB_CUST_ID_PLANNING = aWB_PlanningCustomer_Id
                    aNewRow.WB_CustomerRow_Planning = _.first(_.filter(arrCust, aCust => aCust.cif.WB_CUST_ID == aWB_PlanningCustomer_Id))
                    aNewRow.cif.WB_CUST_ID_SOURCE = aWB_ExternalCustomer_Id
                    aNewRow.WB_CustomerRow_Source = _.first(_.filter(arrCust, aCust => aCust.cif.WB_CUST_ID == aWB_ExternalCustomer_Id))
                    aNewRow.cif.SOURCE_ID = sourceRow.cif?.SOURCE_ID
                    aNewRow.cif.WB_SALESAREA_ID = self.drWB_SalesOrg.Selected_SalesArea
                    aNewRow.cif.COVERAGE_FACTOR = 1
                    self.dbContext.add(esWB_Customer_External_Relationship, aNewRow)
                    resolve(self)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    removeExternalCustomer(aWB_ExtCustR_Id: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {
            _.forEach(self.drWB_SalesOrg.WB_CustomerExternalRelationshipRows, (aRow: WB_CustomerExternalRelationshipRow) => {
                if (aRow.isActive && aRow.cif.WB_EXTCUSTR_ID == aWB_ExtCustR_Id) {
                    aRow.markDeleted()
                }
            })
            resolve(self)
        })
    }

    addLiftAPI(): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {

            self.dbContext.createRecordWithKey(esWB_LiftAPI)
                .then((aNewRow: WB_LiftAPIRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SALESORG_ID = self.drWB_SalesOrg.cif.WB_SALESORG_ID
                    aNewRow.WB_SalesOrgRow = self.WB_SalesOrg
                    self.dbContext.add(esWB_LiftAPI, aNewRow)
                })
                .then(() => {
                    resolve(self)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    removeLiftAPI(aWB_LiftAPI_ID: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {
            _.forEach(self.drWB_SalesOrg.WB_LiftAPIRows, (aRow: WB_LiftAPIRow) => {
                if (aRow.isActive && aRow.cif.WB_LIFTAPI_ID == aWB_LiftAPI_ID) {
                    aRow.markDeleted()
                }
            })
            resolve(self)
        })
    }

    addBMRow(bmId?: number, options?: any): Promise<SalesOrgBO> {
        const self = this
        return new Promise<SalesOrgBO>((resolve, error) => {
            return self.dbContext.createRecordWithKey(esWB_Benchmark)
                .then((aNewRow: WB_BenchmarkRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SALESORG_ID = self.drWB_SalesOrg.cif.WB_SALESORG_ID
                    aNewRow.WB_SalesOrgRow = self.drWB_SalesOrg
                    self.dbContext.add(esWB_Benchmark, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })

    }

    deleteBMRow(aWB_BENCHMARK_ID: number, options: any): Promise<SalesOrgBO> {
        const self = this
        return new Promise<SalesOrgBO>((resolve, error) => {
            _.forEach(self.drWB_SalesOrg.WB_BenchmarkRows, (aBenchmarkRow: WB_BenchmarkRow) => {
                if (aBenchmarkRow.isActive && aBenchmarkRow.cif.WB_BENCHMARK_ID == aWB_BENCHMARK_ID) {
                    aBenchmarkRow.markDeleted()
                }
            })
            resolve(self)
        })
    }

    addTargetRow(targetId?: number, options?: any): Promise<SalesOrgBO> {
        const self = this
        return new Promise<SalesOrgBO>((resolve, error) => {
            return self.dbContext.createRecordWithKey(esWB_Target)
                .then((aNewRow: WB_TargetRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SALESORG_ID = self.drWB_SalesOrg.cif.WB_SALESORG_ID
                    aNewRow.WB_SalesOrgRow = self.drWB_SalesOrg
                    self.dbContext.add(esWB_Target, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })

    }

    deleteTargetRow(targetWbId: number, options: any): Promise<SalesOrgBO> {
        const self = this
        return new Promise<SalesOrgBO>((resolve, error) => {
            _.forEach(self.drWB_SalesOrg.WB_TargetRows, (aTargetRow: WB_TargetRow) => {
                if (aTargetRow.isActive && aTargetRow.cif.WB_TARGET_ID == targetWbId) {
                    aTargetRow.markDeleted()
                }
            })
            resolve(self)
        })
    }


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ("dbContext" in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }

        const business_unit_options: CreateOptions = {
            standardConditions: {
                [esWB_SalesOrg]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_Source]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_Customer_External_Relationship]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_Customer_Calendar]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_SalesArea]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_SalesOrg_Text]: { "DELETED_IND": { "notIn": ["1"] } }
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Customer]: [esWB_Source]
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_SalesOrg]: [esWB_Published_Model],
                [esWB_MasterData_Set]: [esWB_General_Ledger]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, business_unit_options)

        return aRecResolver
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_SalesOrg, ids)
                .then((result) => {
                    resolve()
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(SalesOrgBO, "SalesOrgBO", "SalesOrg")